import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Input, notification, Typography } from 'antd'
import useNewAnnotation from 'hooks/useNewAnnotation'
import ViewerFilePreview from 'pages/building/buildingDetailViewMode/components/ViewerFilePreview'
import { AnnotationType, BuildingFile } from 'types/building'

import Button from 'components/atoms/Button'
import Modal from 'components/atoms/Modal'
import Title from 'components/atoms/Title'

import ConfirmChangeAnnotation from '../ConfirmChangeAnnotation'
import QuillEditorV2 from '../ModalAlertSetting/QuillEditorV2'
import ModalDeleteAnnotation from '../ModalDeleteAnnotation'

import TourAnnotationTypeSelector from './AnnotationTypeSelector'
import FileList from './FileList'

const annotationCache: Record<string | number, any> = {}

export default function TourAnnotationModal() {
  const isValidYoutubeUrl = (url: string) => {
    const YOUTUBE_REGEX = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+/
    return url.startsWith('https://') && !!url.match(YOUTUBE_REGEX)
  }
  const { t } = useTranslation()
  const [isAddingFile, setIsAddingFile] = useState(false)
  const [isChangeType, setIsChangeType] = useState(false)
  const [openModal, setOpenModal] = useState<string>('')
  const [existsFile] = useState<BuildingFile[]>([])
  const [selectedFiles, setSelectedFiles] = useState<BuildingFile[]>([
    ...existsFile
  ])
  const [selectedFileName, setSelectedFileName] = useState<string>('')
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null)
  const [selectedPdfFile, setSelectedPdfFile] = useState<File | null>(null)
  const [previewFileIndex, setPreviewFileIndex] = useState<number | undefined>(
    undefined
  )

  const {
    isLoading,
    // isDataExistsAnnotation,
    dataSource,
    memoHtml,
    setMemoHtml,
    currentSelected,
    setCurrentSelected,
    selectedFolder,
    setSelectedFolder,
    selectedChildrenFolder,
    handleCloseModal,
    handleSubmit
  } = useNewAnnotation()

  const clearAnnotationCache = () => {
    Object.keys(annotationCache).forEach((key) => {
      delete annotationCache[key]
    })
  }

  const handleSubmitWithCacheClear = async (
    imageFile?: File | null,
    pdfFile?: File | null
  ) => {
    let currentMemo = currentSelected?.memo
    if (memoHtml) {
      currentMemo = memoHtml
    }

    const currentAnnotationData = {
      ...currentSelected,
      type: selectedFolder,
      memo: currentMemo,
      memo_file_name: selectedFileName,
      files: selectedFiles
    }

    const originalCurrentSelected = currentSelected

    setCurrentSelected(currentAnnotationData)

    try {
      if (selectedFolder === AnnotationType.MOVIE) {
        const youtubeUrl = currentSelected?.link || ''
        if (youtubeUrl.trim() && !isValidYoutubeUrl(youtubeUrl)) {
          notification.error({
            message: t('translation.annotationYoutubeUrlRequired'),
            duration: 3
          })
          return
        }
        if (youtubeUrl.trim() && youtubeUrl.length > 1500) {
          notification.error({
            message: t('translation.annotationYoutubeUrlMaxLength'),
            duration: 3
          })
          return
        }
      }
      await handleSubmit(imageFile, pdfFile)
      clearAnnotationCache()
    } catch (error) {
      setCurrentSelected(originalCurrentSelected)
      throw error
    }
  }

  const changeSelectedFolder = (value: any) => {
    if (value !== selectedFolder) {
      if (selectedFolder) {
        let latestMemo = currentSelected?.memo
        if (memoHtml) {
          latestMemo = memoHtml
        }
        annotationCache[selectedFolder] = {
          ...currentSelected,
          files: selectedFiles,
          memo_file_name: selectedFileName,
          image_file: selectedImageFile,
          pdf_file: selectedPdfFile,
          memo: latestMemo,
          type: selectedFolder
        }
      }

      // Reset HTML file input values
      const imageInput = document.getElementById(
        'image-upload'
      ) as HTMLInputElement
      const pdfInput = document.getElementById(
        'pdf-file-input'
      ) as HTMLInputElement
      if (imageInput) imageInput.value = ''
      if (pdfInput) pdfInput.value = ''

      const found = annotationCache[value]
      if (found) {
        setCurrentSelected({
          ...found,
          link: found.link || '',
          memo_file_name: found.memo_file_name || ''
        })
        setSelectedFiles(found.files || [])
        setSelectedFileName(found.memo_file_name || '')
        setSelectedImageFile(found.image_file)
        setSelectedPdfFile(found.pdf_file)
        setSelectedFolder(found.type)
        setMemoHtml(found.memo || '')
      } else {
        setCurrentSelected((prev) => ({
          ...prev,
          type: value,
          link: '',
          memo: '',
          memo_file_name: '',
          image_file: '',
          pdf_file: ''
        }))
        setSelectedFiles([])
        setSelectedFileName('')
        setSelectedImageFile(null)
        setSelectedPdfFile(null)
        setSelectedFolder(value)
        setMemoHtml('')
      }
    }
  }

  const handleCloseFileSelectMenu = (clear?: boolean) => {
    if (clear) {
      setSelectedFiles([])
    } else {
      const filesPrev =
        currentSelected?.files?.filter(
          (file) => !existsFile.map((item) => item.id).includes(file.id)
        ) || []
      setCurrentSelected((prev) => ({
        ...prev,
        files: [...filesPrev, ...selectedFiles]
      }))
    }
    setIsAddingFile(false)
  }

  const handleSelectFile = (file: BuildingFile) => {
    // Try to find by ID first (most stable) then by Name (for newly uploaded local blobs)
    const existingEntry = selectedFiles.find(
      (item) => item.id === file.id || item.name === file.name
    )

    if (existingEntry) {
      // Use the specific ID found in state to filter it out reliably
      setSelectedFiles((prev) =>
        prev.filter((item) => item.id !== existingEntry.id)
      )
      return
    }

    const overwrittenEntry = currentSelected?.files?.find(
      (item) => item.name === file.name
    )

    setSelectedFiles((prev) => [
      ...prev,
      {
        ...(overwrittenEntry || file),
        category: selectedChildrenFolder?.id
      }
    ])
  }

  useEffect(() => {
    if (existsFile.length) {
      setSelectedFiles([...existsFile])
    }
  }, [existsFile])

  useEffect(() => {
    const isValidFileForType = (fileName: string, type: AnnotationType) => {
      const extension = fileName.toLowerCase().split('.').pop()
      if (type === AnnotationType.IMAGE) {
        return ['jpg', 'jpeg', 'png', 'gif'].includes(extension || '')
      }
      if (type === AnnotationType.INFO) {
        return extension === 'pdf'
      }
      return false
    }

    if (
      currentSelected?.memo_file_name &&
      (selectedFolder === AnnotationType.IMAGE ||
        selectedFolder === AnnotationType.INFO) &&
      isValidFileForType(currentSelected.memo_file_name, selectedFolder)
    ) {
      setSelectedFileName(currentSelected.memo_file_name)
    } else {
      setSelectedFileName('')
    }
  }, [currentSelected?.memo_file_name, currentSelected?.id, selectedFolder])

  const handleCloseModalWithCache = () => {
    if (typeof handleCloseModal === 'function') {
      handleCloseModal()
    }
    clearAnnotationCache()
    setSelectedFiles([])
    setSelectedFileName('')
    setSelectedImageFile(null)
    setSelectedPdfFile(null)
    setOpenModal('')
    setIsAddingFile(false)
    setIsChangeType(false)
  }

  return (
    <>
      <Modal
        width="840px"
        rootClassName="shared-permission-modal"
        onCancel={handleCloseModalWithCache}
      >
        {isAddingFile ? (
          <FileList
            folder={dataSource}
            selectedChildrenFolder={selectedChildrenFolder}
            selectedFiles={selectedFiles}
            handleSelectFile={handleSelectFile}
            handleCloseFileSelectMenu={handleCloseFileSelectMenu}
          />
        ) : (
          <>
            <div className="flex flex-col w-[45rem]">
              <Title
                height={28.96}
                title={t('building.titleAddTourAnnotation')}
                bottomMargin="!mb-0"
              />
              <TourAnnotationTypeSelector
                changeSelectedFolder={changeSelectedFolder}
                selectedFolder={selectedFolder}
              />
              <div className="flex flex-col min-h-[430px] max-h-[430px]">
                {selectedFolder === AnnotationType.IMAGE ? (
                  <>
                    <div className="flex pt-[25px] justify-between w-full">
                      <Typography.Paragraph className="pt-[5px] ml-[90px] font-bold">
                        画像
                      </Typography.Paragraph>
                      <Typography.Paragraph className="pt-[5px] ml-[90px] text-[14px] truncate text-wrap max-w-[320px]">
                        {selectedFileName || 'ファイルが選択されていません'}
                      </Typography.Paragraph>
                      <div className="flex flex-col justify-center items-center">
                        <input
                          type="file"
                          accept=".jpg,.jpeg,.png,.JPG,.JPEG,.PNG"
                          style={{ display: 'none' }}
                          id="image-upload"
                          onChange={(e) => {
                            const file = e.target.files?.[0]
                            if (file) {
                              const ext = file.name
                                .toLowerCase()
                                .split('.')
                                .pop()
                              const allowedExt = ['jpg', 'jpeg', 'png']
                              if (!allowedExt.includes(ext || '')) {
                                notification.error({
                                  message:
                                    '画像ファイルはjpg、jpeg、png形式のみ対応しています。'
                                })
                                e.target.value = ''
                                return
                              }
                              if (file.size / (1024 * 1024) > 50) {
                                notification.error({
                                  message: t(
                                    'common.validationError.fileTooLarge'
                                  )
                                })
                                e.target.value = ''
                                return
                              }
                              setSelectedFileName(file.name)
                              setSelectedImageFile(file)
                            }
                            e.target.value = ''
                          }}
                        />
                        <Button
                          onClick={() =>
                            document.getElementById('image-upload')?.click()
                          }
                          className="text-[#000] bg-[#B3CFCB] font-light text-[16px]"
                        >
                          選択
                        </Button>
                        <Typography.Paragraph className="text-[12px]">
                          ファイル容量上限: 50MBまで
                        </Typography.Paragraph>
                      </div>
                    </div>
                    <Typography.Paragraph className="pt-[5px] px-[90px] !mb-[0px] font-bold text-[14px]">
                      {t('building.titleAddTourAnnotationText')}
                    </Typography.Paragraph>
                  </>
                ) : null}
                {selectedFolder === AnnotationType.MOVIE ? (
                  <>
                    <Input
                      key={selectedFolder}
                      placeholder={t('building.placeholderTourAnnotationMovie')}
                      className="mt-[10px] ml-32 w-[450px]"
                      value={currentSelected?.memo_content || ''}
                      onChange={(e) => {
                        setCurrentSelected((prev) => ({
                          ...prev,
                          memo_content: e.target.value
                        }))
                      }}
                    />
                    <Typography.Paragraph className="ml-32 text-[14px]">
                      {t('building.titleAddTourAnnotationMovie')}
                    </Typography.Paragraph>
                    <Typography.Paragraph className="pt-[5px] px-[90px] !mb-[0px] font-bold text-[14px]">
                      {t('building.titleAddTourAnnotationText')}
                    </Typography.Paragraph>
                  </>
                ) : null}
                {selectedFolder === AnnotationType.INFO ? (
                  <div className="flex pt-[25px] justify-between w-full">
                    <Typography.Paragraph className="pt-[5px] ml-[90px] text-[14px] font-normal truncate text-wrap max-w-[60%]">
                      {selectedFileName ||
                        t('building.titleAddTourAnnotationInfo')}
                    </Typography.Paragraph>
                    <div className="flex flex-col justify-center items-center">
                      <input
                        type="file"
                        accept=".pdf"
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          const file = e.target.files?.[0]
                          if (file) {
                            const ext = file.name.toLowerCase().split('.').pop()
                            if (ext !== 'pdf') {
                              notification.error({
                                message: 'PDFファイルのみ選択できます。'
                              })
                              e.target.value = ''
                              setSelectedFileName('')
                              setSelectedPdfFile(null)
                              return
                            }
                            if (file.size / (1024 * 1024) > 50) {
                              notification.error({
                                message: t(
                                  'common.validationError.fileTooLarge'
                                )
                              })
                              e.target.value = ''
                              setSelectedFileName('')
                              setSelectedPdfFile(null)
                              return
                            }
                            setSelectedFileName(file.name)
                            setSelectedPdfFile(file)
                          }
                          e.target.value = ''
                        }}
                        ref={(input) => {
                          if (input) {
                            input.id = 'pdf-file-input'
                          }
                        }}
                      />

                      <Button
                        onClick={() => {
                          document.getElementById('pdf-file-input')?.click()
                        }}
                        className="text-[#000] bg-[#B3CFCB] font-light text-[16px]"
                      >
                        {t('building.businessSetting.selectFileBtn')}
                      </Button>
                      <Typography.Paragraph className="text-[10px]">
                        {t('building.businessSetting.pdfFileOnly')}
                      </Typography.Paragraph>
                    </div>
                  </div>
                ) : null}
                <div id="annotation-memo-setting" className="relative">
                  <div
                    className={`${
                      selectedFolder === AnnotationType.MOVIE ||
                      selectedFolder === AnnotationType.IMAGE
                        ? 'visible'
                        : 'invisible'
                    } pt-[5px] px-[90px]`}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%'
                    }}
                  >
                    <QuillEditorV2
                      value={memoHtml}
                      onChange={setMemoHtml}
                      height={280}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="flex w-full items-end pt-5 pl-5 pr-[30px] mt-[38px] justify-between border-0 border-t border-solid border-[#EEEFF2] mx-2">
              {currentSelected?.id && (
                <Button
                  onClick={() => setOpenModal('deleted')}
                  sizing="w-[140px] h-[50px]"
                  className="bg-[#B80808]"
                >
                  {t('common.delete')}
                </Button>
              )}

              <div className="flex gap-10 ml-auto">
                <Button
                  sizing="w-[140px] h-[50px]"
                  onClick={handleCloseModalWithCache}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  sizing="w-[140px] h-[50px]"
                  className="theme-bg-primary"
                  loading={isLoading}
                  onClick={() => {
                    handleSubmitWithCacheClear(
                      selectedImageFile,
                      selectedPdfFile
                    )
                  }}
                >
                  {t('common.confirm')}
                </Button>
              </div>
            </div>
          </>
        )}
      </Modal>
      {openModal === 'deleted' && (
        <ModalDeleteAnnotation
          setIsOpen={setOpenModal}
          handleCloseModal={handleCloseModal}
        />
      )}
      {isChangeType && (
        <ConfirmChangeAnnotation
          selectedFolder={selectedFolder}
          setSelectedFolder={setSelectedFolder}
          setIsOpenConfirm={setIsChangeType}
        />
      )}
      {previewFileIndex !== undefined && (
        <ViewerFilePreview
          fileIndex={previewFileIndex}
          file={existsFile[previewFileIndex]}
          maxLength={existsFile.length}
          setPreviewFileIndex={setPreviewFileIndex}
        />
      )}
    </>
  )
}
